import { COUNTRY_CODES, CountryCode } from "@component/country-codes-data";
import { applicationStatusMap } from "@constant/app";
import { WorkExperience } from "@type/candidate";
import { CVData } from "@type/cv";
import { CalendarIcon, CheckCircle, MailIcon, UserCheckIcon, UserXIcon } from "lucide-react";
import { marked } from "marked";

export const convertContentBreakline = (content: string): string => {
  return content.replace(/\n/g, "<br />");
};

export function parsePhoneNumber(fullNumber: string): {
  code: CountryCode;
  number: string;
} {
  const matchedCode = COUNTRY_CODES.find((code) =>
    fullNumber.startsWith(code.code)
  );
  if (!matchedCode) {
    return {
      code: {
        code: "+1",
        flag: "🇺🇸",
        name: "United States",
      },
      number: "",
    };
  }

  return {
    code: matchedCode,
    number: fullNumber.slice(matchedCode.code.length),
  };
}

export const getStatusBadgeClass = (status: string) => {
  switch (status) {
    case "Offer":
    case "Accepted":
    case "Complete":
    case "Published":
    case "Public":
    case "Active":
      return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400";
    case "Pending":
      return "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400";
    case "Rejected":
    case "Deleted":
    case "Closed":
    case "Rejected Offer":
    case "Aborted":
      return "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400";
    case "Pending Review":
    case "Pending Decision":
    case "Pending Verification":
    case "Pending Interview":
    case "Interviewer Ready":
    case "None":
      return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400";
    case "Incomplete":
    case "Partial":
    case "Paused":
      return "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400";
    default:
      return "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400";
  }
};

export const getApplicationStatusClass = (status: string) => {
  switch (status) {
    case "Offer Accepted":
    case "Offer Sent":
    case "Accepted":
      return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400";
    case "Pending Review":
    case "Invited to Apply":
      return "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400";
    case "Rejected":
    case "Offer Rejected":
    case "CV Rejected":
    case "Assessment Rejected":
    case "AI Interview Rejected":
      return "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400";
    case "Hired":
      return "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400";
    case "Interview Sent":
    case "Interview Resent":
      return "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400";
    case "Offer Resent":
    case "Invitation Expired":
    case "Draft":
      return "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400";
    case "Application Reopened":
      return "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400";
    default:
      return "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400";
  }
};

export const getInvitationBannerClass = (status: string) => {
  switch (status) {
    case "Invited to Apply":
      return "bg-blue-50 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400";
    case "Invitation Expired":
    case "Invitation Expired - FU sent":
      return "bg-orange-50 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400";
    default:
      return "bg-gray-50 text-gray-800 dark:bg-gray-800 dark:text-gray-400";
  }
};

export const getInvitationBorderClass = (status: string) => {
  switch (status) {
    case "Invited to Apply":
      return "border-blue-200";
    case "Invitation Expired":
    case "Invitation Expired - FU sent":
      return "border-orange-200";
    default:
      return "border-gray-200";
  }
};

export const getBonusStatusColor = (status: string) => {
  switch (status) {
    case "In Progress":
    case "Pending Payment":
      return "bg-yellow-100 text-yellow-800 border-yellow-200";
    case "Paid":
      return "bg-green-100 text-green-800 border-green-200";
    case "Ineligible":
      return "bg-red-100 text-red-800 border-red-200";
    default:
      return "bg-gray-100 text-gray-800 border-gray-200";
  }
};

export const getPayrollStatusColor = (status: string) => {
  switch (status) {
    case "pending":
      return "bg-yellow-100 text-yellow-800 border-yellow-200";
    case "processing":
      return "bg-blue-100 text-blue-800 border-blue-200";
    case "paid":
      return "bg-green-100 text-green-800 border-green-200";
    default:
      return "bg-gray-100 text-gray-800 border-gray-200";
  }
};

export const getBannerContent = (status: string, date: string) => {
  switch (status) {
    case applicationStatusMap.interviewSent:
    case applicationStatusMap.interviewResent:
      return {
        applicationStatusText: `You sent an interview invitation on ${date}`,
        applicationStatusBgColor: "bg-blue-50",
        applicationStatusTextColor: "text-blue-700",
        applicationStatusBorderColor: "border-blue-200",
      };

    case applicationStatusMap.offerSent:
      return {
        applicationStatusText: `You sent an offer on ${date}`,
        applicationStatusBgColor: "bg-green-50",
        applicationStatusTextColor: "text-green-700",
        applicationStatusBorderColor: "border-green-200",
      };
    case applicationStatusMap.rejected:
      return {
        applicationStatusText: `You sent a rejection on ${date}`,
        applicationStatusBgColor: "bg-gray-50",
        applicationStatusTextColor: "text-gray-700",
        applicationStatusBorderColor: "border-gray-200",
      };
    case applicationStatusMap.offerRejected:
      return {
        applicationStatusText: `Candidate declined the offer on ${date}`,
        applicationStatusBgColor: "bg-amber-50",
        applicationStatusTextColor: "text-amber-700",
        applicationStatusBorderColor: "border-amber-200",
      };
    case applicationStatusMap.offerAccepted:
      return {
        applicationStatusText: `Candidate accepted the offer on ${date}`,
        applicationStatusBgColor: "bg-green-50",
        applicationStatusTextColor: "text-green-700",
        applicationStatusBorderColor: "border-green-200",
      };
    case applicationStatusMap.offerResent:
      return {
        applicationStatusText: `Offer resent on ${date}`,
        applicationStatusBgColor: "bg-green-50",
        applicationStatusTextColor: "text-green-700",
        applicationStatusBorderColor: "border-green-200",
      };
    case applicationStatusMap.applicationReopened:
      return {
        applicationStatusText: `Application reopened on ${date}`,
        applicationStatusBgColor: "bg-purple-50",
        applicationStatusTextColor: "text-purple-700",
        applicationStatusBorderColor: "border-purple-200",
      };
    case applicationStatusMap.hired:
      return {
        applicationStatusText: `Hired on ${date}`,
        applicationStatusBgColor: "bg-green-50",
        applicationStatusTextColor: "text-green-700",
        applicationStatusBorderColor: "border-green-200",
      };
    default:
      return {
        applicationStatusText: "Application pending review",
        applicationStatusBgColor: "bg-gray-50",
        applicationStatusTextColor: "text-gray-700",
        applicationStatusBorderColor: "border-gray-200",
      };
  }
};

export const getTotalWorkExperience = (
  workExperience: WorkExperience[]
): number => {
  if (!workExperience?.length) return 0;

  const sortedExperience = [...workExperience].sort((a, b) => {
    if (a.period_end_year === 9999) return -1;
    if (b.period_end_year === 9999) return 1;
    return b.period_start_year - a.period_start_year;
  });

  const latestExperience = sortedExperience[0];
  const firstExperience = sortedExperience[sortedExperience.length - 1];
  const startYear = firstExperience.period_start_year;
  let endYear = latestExperience.period_end_year;

  if (endYear === 9999) {
    endYear = new Date().getFullYear();
  }

  return Math.max(0, endYear - startYear);
};

export const getBrowserName = () => {
  if (typeof window === "undefined") return { name: "", version: "" };

  const userAgent = window.navigator.userAgent;
  const userAgentData = (navigator as any).userAgentData;

  if (userAgentData) {
    const brands = userAgentData.brands || userAgentData.uaList || [];
    if (brands.length === 2) {
      return { name: "Arc", version: "0" };
    }
    for (const brand of brands) {
      if (brand.brand.includes("Chromium")) continue;
      if (brand.brand.includes("Google Chrome")) {
        return { name: "Chrome", version: brand.version };
      }
      if (brand.brand.includes("Microsoft Edge")) {
        return { name: "Edge", version: brand.version };
      }
    }
  }

  // Fallback ke userAgent biasa
  let match: RegExpMatchArray | null;
  if ((match = userAgent.match(/Firefox\/([0-9.]+)/)))
    return { name: "Firefox", version: match[1] };
  if ((match = userAgent.match(/Edg\/([0-9.]+)/)))
    return { name: "Edge", version: match[1] };
  if ((match = userAgent.match(/Chrome\/([0-9.]+)/)))
    return { name: "Chrome", version: match[1] };
  if ((match = userAgent.match(/Version\/([0-9.]+).*Safari/)))
    return { name: "Safari", version: match[1] };
  if ((match = userAgent.match(/Mozilla\/([0-9.]+)/)))
    return { name: "Mozilla", version: match[1] };
  if (
    (match = userAgent.match(/MSIE ([0-9.]+)/)) ||
    (match = userAgent.match(/rv:([0-9.]+)\) like Gecko/))
  )
    return { name: "Internet Explorer", version: match[1] };

  return { name: "Unknown", version: "" };
};

export const formatCV = (cvData: CVData | null) => {
  if (!cvData) return "No CV data available.";

  let formattedCV = "";
  const cv = cvData;

  if (!cv) return "No CV data available.";

  // Format candidate name and position
  formattedCV += `Name: ${cv.name || "Not provided"}\n`;
  formattedCV += `Position: ${cv.position || "Not provided"}\n\n`;

  // Format contact info
  formattedCV += `Email: ${cv.email || "Not provided"}\n`;
  formattedCV += `Phone: ${cv.phone || "Not provided"}\n\n`;

  // Format professional summary
  if (cv.professional_summary) {
    formattedCV += `Summary: ${cv.professional_summary}\n\n`;
  }

  // Format skills
  if (cv.skills && cv.skills.length > 0) {
    formattedCV += "Skills:\n";
    cv.skills.forEach((skill: string) => {
      formattedCV += `- ${skill}\n`;
    });
    formattedCV += "\n";
  }

  // Format work experience
  if (cv.work_experience && cv.work_experience.length > 0) {
    formattedCV += "Work Experience:\n";
    cv.work_experience.forEach((exp: any) => {
      formattedCV += `- ${exp.role} at ${exp.institution} (${exp.period})\n`;
      if (exp.description) {
        formattedCV += `  ${exp.description}\n`;
      }
    });
    formattedCV += "\n";
  }

  // Format education
  if (cv.education && cv.education.length > 0) {
    formattedCV += "Education:\n";
    cv.education.forEach((edu: any) => {
      formattedCV += `- ${edu.degree} from ${edu.institution} (${edu.period})\n`;
      if (edu.description) {
        formattedCV += `  ${edu.description}\n`;
      }
    });
  }

  return formattedCV;
};

export const isValidEmail = (email: string) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const inArrayString = (array: string[], value: string): boolean => {
  return array.includes(value);
};

export const getCategoryIcon = (type: string) => {
  switch (type) {
    case "reject":
      return UserXIcon;
    case "hire":
      return UserCheckIcon;
    case "interview-invitation":
      return CalendarIcon;
    case "job-invitation":
      return MailIcon;
    case "assessment-invitation":
      return CheckCircle;
    default:
      return MailIcon;
  }
};

export const getCategoryColor = (type: string) => {
  switch (type) {
    case "reject":
      return "text-red-600";
    case "hire":
      return "text-green-600";
    case "interview-invitation":
      return "text-blue-600";
    case "job-invitation":
      return "text-purple-600";
    case "assessment-invitation":
      return "text-amber-600";
    default:
      return "text-gray-600";
  }
};

export const convertToLowerSnakeCase = (str: string | null | undefined): string => {
  if (!str) return "";
  return str
    .replace(/\s+/g, "_") // Ganti spasi dengan underscore
    .toLowerCase(); // Ubah semua huruf menjadi kecil
}

export const convertCapitalizeWords = (str: string | null | undefined): string => {
  if (!str) return "";
  return str
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

// HR-facing copy renamed "Assessment" → "Screening", but activity-log text
// (action_header/title/description) and some action_stage strings still arrive
// from the BE with the old wording. Display-only, case-preserving rewrite —
// the BE data itself keeps the old term by product decision (FE-only rename).
export const renameAssessmentToScreening = (text: string | null | undefined): string => {
  if (!text) return "";
  return text.replace(/assessment/gi, (match) => {
    if (match === match.toUpperCase()) return "SCREENING";
    if (match.charAt(0) === "A") return "Screening";
    return "screening";
  });
}

export const getFunnelStatus = (stage: string): string[] => {
  let filterStatus: string[] = [];
  if (stage === "uploads") {
    filterStatus = [
      "Upload Complete",
      "Shortlisted",
      "On Hold",
      "Not Suitable",
    ]
  } else if (stage === "applied") {
    filterStatus = [
      "CV Parsed",
      // Pre-flow entry status for auto-start hiring flows (#3638 §4.2):
      // CV-less candidates transit through Applied and stay visible here only
      // when the flow could not start (send failed / no opt-in).
      "Applied",
      "Shortlisted",
      "On Hold",
      "Not Suitable",
    ]
  } else if (stage === "pending_assessment") {
    filterStatus = [
      "Pending Assessment",
      "Link Expired",
      "Form Incomplete",
      "Interview Incomplete",
      "Aborted Interview",
      "Partial Interview",
      // Hiring-flow gate holds (#3074) — the ONLY two statuses the
      // Customizable Hiring Flow adds; both live in this bucket and feed the
      // Flagged sub-view. Set by the BE gate engine, resolved by HR.
      "Knockout Flagged",
      "Deadline Missed",
      // #3249 (BE #3991): live while Maya's outbound phone interview is running;
      // the BE restores the prior status when the call ends.
      "Calling in Progress",
    ]
  } else if (stage === "to_review") {
    filterStatus = [
      "Pending Review",
      "Pending Decision",
      "Review Scheduled",
    ]
  } else if (stage === "offers") {
    filterStatus = [
      "Offer Sent",
      "Offer Resent",
      "Offer Declined"
    ]
  } else if (stage === "closed") {
    filterStatus = [
      "Rejected",
      "Hired",
      "Assessment Rejected",
      "CV Rejected",
      "AI Interview Rejected",
      "Archived"
    ]
  }
  return filterStatus;
}

export const getFunnelStageByStatus = (status: string): string => {
  const statusToStage: Record<string, string> = {
    "Upload Complete": "Uploads",
    "Shortlisted": "Uploads",
    "On Hold": "Uploads",
    "Not Suitable": "Uploads",
    "CV Parsed": "Applied",
    "Applied": "Applied",
    "Pending Assessment": "Screening in Progress",
    "Link Expired": "Screening in Progress",
    "Form Incomplete": "Screening in Progress",
    "Interview Incomplete": "Screening in Progress",
    "Aborted Interview": "Screening in Progress",
    "Partial Interview": "Screening in Progress",
    "Knockout Flagged": "Screening in Progress",
    "Deadline Missed": "Screening in Progress",
    "Calling in Progress": "Screening in Progress",
    "Pending Review": "Ready for Review",
    "Pending Decision": "Ready for Review",
    "Review Scheduled": "Ready for Review",
    "Offer Sent": "Offers",
    "Offer Resent": "Offers",
    "Offer Declined": "Offers",
    "Rejected": "Closed",
    "Hired": "Closed",
    "Assessment Rejected": "Closed",
    "CV Rejected": "Closed",
    "AI Interview Rejected": "Closed",
    "Archived": "Closed",
  };
  return statusToStage[status] || "";
}

export const getApplicantStatusBadge = (status: string) => {
  const statusConfig: Record<string, { label: string; className: string; classNameChip: string }> = {
    upload_complete: {
      label: "Upload Complete",
      className: "bg-gray-100 hover:bg-gray-200 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300",
      classNameChip: "bg-gray-600 hover:bg-gray-700 text-white shadow-md !ring-2 ring-gray-600 !ring-offset-2"
    },
    complete: {
      label: "Complete",
      className:
        "bg-green-100 hover:bg-green-200 text-green-800 dark:bg-green-900/30 dark:text-green-300",
      classNameChip: "bg-green-600 hover:bg-green-700 text-white shadow-md !ring-2 ring-green-600 dark:ring-green-500 !ring-offset-2"
    },
    cv_parsed: {
      label: "CV Parsed",
      className:
        "bg-gray-100 hover:bg-gray-200 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300",
      classNameChip: "bg-gray-600 hover:bg-gray-700 text-white shadow-md !ring-2 ring-gray-600 dark:ring-gray-500 !ring-offset-2"
    },
    pending_review: {
      label: "Pending Review",
      className:
        "bg-gray-100 hover:bg-gray-200 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300",
      classNameChip: "bg-gray-600 hover:bg-gray-700 text-white shadow-md !ring-2 ring-gray-600 dark:ring-gray-500 !ring-offset-2"
    },
    offer_sent: {
      label: "Offer Sent",
      className:
        "bg-green-100 hover:bg-green-200 text-green-800 dark:bg-green-900/30 dark:text-green-300",
      classNameChip: "bg-green-600 hover:bg-green-700 text-white shadow-md !ring-2 ring-green-600 dark:ring-green-500 !ring-offset-2"
    },
    offer_rejected: {
      label: "Rejected",
      className:
        "bg-red-100 hover:bg-red-200 text-red-800 dark:bg-red-900/30 dark:text-red-300",
      classNameChip: "bg-red-600 hover:bg-red-700 text-white shadow-md !ring-2 ring-red-600 dark:ring-red-500 !ring-offset-2"
    },
    rejected: {
      label: "Rejected",
      className:
        "bg-red-100 hover:bg-red-200 text-red-800 dark:bg-red-900/30 dark:text-red-300",
      classNameChip: "bg-red-600 hover:bg-red-700 text-white shadow-md !ring-2 ring-red-600 dark:ring-red-500 !ring-offset-2"
    },
    assessment_rejected: {
      label: "Rejected",
      className:
        "bg-red-100 hover:bg-red-200 text-red-800 dark:bg-red-900/30 dark:text-red-300",
      classNameChip: "bg-red-600 hover:bg-red-700 text-white shadow-md !ring-2 ring-red-600 dark:ring-red-500 !ring-offset-2"
    },
    cv_rejected: {
      label: "Rejected",
      className:
        "bg-red-100 hover:bg-red-200 text-red-800 dark:bg-red-900/30 dark:text-red-300",
      classNameChip: "bg-red-600 hover:bg-red-700 text-white shadow-md !ring-2 ring-red-600 dark:ring-red-500 !ring-offset-2"
    },
    ai_interview_rejected: {
      label: "Rejected",
      className:
        "bg-red-100 hover:bg-red-200 text-red-800 dark:bg-red-900/30 dark:text-red-300",
      classNameChip: "bg-red-600 hover:bg-red-700 text-white shadow-md !ring-2 ring-red-600 dark:ring-red-500 !ring-offset-2"
    },

    invited: {
      label: "Invited",
      className:
        "bg-purple-100 hover:bg-purple-200 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300",
      classNameChip: "bg-purple-600 hover:bg-purple-700 text-white shadow-md !ring-2 ring-purple-600 dark:ring-purple-500 !ring-offset-2"
    },
    invitation_expired: {
      label: "Invitation Expired",
      className:
        "bg-red-100 hover:bg-red-200 text-red-800 dark:bg-red-900/30 dark:text-red-300",
      classNameChip: "bg-red-600 hover:bg-red-700 text-white shadow-md !ring-2 ring-red-600 dark:ring-red-500 !ring-offset-2"
    },

    // Needs Review tab statuses
    review_scheduled: {
      label: "Review Scheduled",
      className:
        "bg-blue-100 hover:bg-blue-200 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300",
      classNameChip: "bg-blue-600 hover:bg-blue-700 text-white shadow-md !ring-2 ring-blue-600 dark:ring-blue-500 !ring-offset-2"
    },
    pending_decision: {
      label: "Pending Decision",
      className:
        "bg-yellow-100 hover:bg-yellow-200 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300",
      classNameChip: "bg-yellow-600 hover:bg-yellow-700 text-white shadow-md !ring-2 ring-yellow-600 dark:ring-yellow-500 !ring-offset-2"
    },

    // Closed tab statuses
    offer_declined: {
      label: "Offer Declined",
      className:
        "bg-orange-100 hover:bg-orange-200 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300",
      classNameChip: "bg-orange-600 hover:bg-orange-700 text-white shadow-md !ring-2 ring-orange-600 dark:ring-orange-500 !ring-offset-2"
    },

    closed: {
      label: "Closed",
      className:
        "bg-gray-100 hover:bg-gray-200 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300",
      classNameChip: "bg-gray-600 hover:bg-gray-700 text-white shadow-md !ring-2 ring-gray-600 dark:ring-gray-500 !ring-offset-2"
    },

    hired: {
      label: "Hired",
      className:
        "bg-green-100 hover:bg-green-200 text-green-800 dark:bg-green-900/30 dark:text-green-300",
      classNameChip: "bg-green-600 hover:bg-green-700 text-white shadow-md !ring-2 ring-green-600 dark:ring-green-500 !ring-offset-2"
    },
    // Assessment tab statuses
    pending_assessment: {
      label: "Pending Screening",
      className:
        "bg-yellow-100 hover:bg-yellow-200 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300",
      classNameChip: "bg-yellow-600 hover:bg-yellow-700 text-white shadow-md !ring-2 ring-yellow-600 dark:ring-yellow-500 !ring-offset-2"
    },
    link_expired: {
      label: "Link Expired",
      className:
        "bg-red-100 hover:bg-red-200 text-red-800 dark:bg-red-900/30 dark:text-red-300",
      classNameChip: "bg-red-600 hover:bg-red-700 text-white shadow-md !ring-2 ring-red-600 dark:ring-red-500 !ring-offset-2"
    },
    form_incomplete: {
      label: "Form Incomplete",
      className:
        "bg-yellow-100 hover:bg-yellow-200 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300",
      classNameChip: "bg-yellow-600 hover:bg-yellow-700 text-white shadow-md !ring-2 ring-yellow-600 dark:ring-yellow-500 !ring-offset-2"
    },
    interview_incomplete: {
      label: "Interview Incomplete",
      className:
        "bg-yellow-100 hover:bg-yellow-200 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300",
      classNameChip: "bg-yellow-600 hover:bg-yellow-700 text-white shadow-md !ring-2 ring-yellow-600 dark:ring-yellow-500 !ring-offset-2"
    },
    aborted_interview: {
      label: "Aborted Interview",
      className:
        "bg-red-100 hover:bg-red-200 text-red-800 dark:bg-red-900/30 dark:text-red-300",
      classNameChip: "bg-red-600 hover:bg-red-700 text-white shadow-md !ring-2 ring-red-600 dark:ring-red-500 !ring-offset-2"
    },
    partial_interview: {
      label: "Partial Interview",
      className:
        "bg-orange-100 hover:bg-orange-200 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300",
      classNameChip: "bg-orange-600 hover:bg-orange-700 text-white shadow-md !ring-2 ring-orange-600 dark:ring-orange-500 !ring-offset-2"
    },
    // Hiring-flow gate holds (#3074) — amber "flagged, paused for HR" pair.
    // Knockout Flagged: an answer gate failed (HR: keep in job | archive).
    // Deadline Missed: a deadline gate expired (HR: extend | next intake | archive).
    knockout_flagged: {
      label: "Knockout Flagged",
      className:
        "bg-amber-100 hover:bg-amber-200 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300",
      classNameChip: "bg-amber-600 hover:bg-amber-700 text-white shadow-md !ring-2 ring-amber-600 dark:ring-amber-500 !ring-offset-2"
    },
    deadline_missed: {
      label: "Deadline Missed",
      className:
        "bg-amber-100 hover:bg-amber-200 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300",
      classNameChip: "bg-amber-600 hover:bg-amber-700 text-white shadow-md !ring-2 ring-amber-600 dark:ring-amber-500 !ring-offset-2"
    },
    // #3249 (BE #3991): a call is live RIGHT NOW — distinct sky "live" treatment,
    // not the generic assessment colour.
    calling_in_progress: {
      label: "Calling in Progress",
      className:
        "bg-sky-100 hover:bg-sky-200 text-sky-800 dark:bg-sky-900/30 dark:text-sky-300",
      classNameChip: "bg-sky-600 hover:bg-sky-700 text-white shadow-md !ring-2 ring-sky-600 dark:ring-sky-500 !ring-offset-2"
    },
    assessment_completed: {
      label: "Screening Completed",
      className:
        "bg-green-100 hover:bg-green-200 text-green-800 dark:bg-green-900/30 dark:text-green-300",
      classNameChip: "bg-green-600 hover:bg-green-700 text-white shadow-md !ring-2 ring-green-600 dark:ring-green-500 !ring-offset-2"
    },
    archived: {
      label: "Archived",
      className:
        "bg-gray-100 hover:bg-gray-200 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300",
      classNameChip: "bg-gray-600 hover:bg-gray-700 text-white shadow-md !ring-2 ring-gray-600 dark:ring-gray-500 !ring-offset-2"
    },
    // Intake stage manual statuses (uploads + applied)
    // NOTE for BE: shortlisted, on_hold, not_suitable must be supported for both
    // the "uploads" and "applied" funnel stages — currently they only exist in one.
    shortlisted: {
      label: "Shortlisted",
      className:
        "bg-green-100 hover:bg-green-200 text-green-800 dark:bg-green-900/30 dark:text-green-300",
      classNameChip: "bg-green-600 hover:bg-green-700 text-white shadow-md !ring-2 ring-green-600 dark:ring-green-500 !ring-offset-2"
    },
    on_hold: {
      label: "On Hold",
      className:
        "bg-yellow-100 hover:bg-yellow-200 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300",
      classNameChip: "bg-yellow-600 hover:bg-yellow-700 text-white shadow-md !ring-2 ring-yellow-600 dark:ring-yellow-500 !ring-offset-2"
    },
    not_suitable: {
      label: "Not Suitable",
      className:
        "bg-red-100 hover:bg-red-200 text-red-800 dark:bg-red-900/30 dark:text-red-300",
      classNameChip: "bg-red-600 hover:bg-red-700 text-white shadow-md !ring-2 ring-red-600 dark:ring-red-500 !ring-offset-2"
    },
  };

  return (
    statusConfig[status] || {
      label: status
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" "),
      className:
        "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300",
      classNameChip: "bg-gray-600 text-white shadow-md ring-2 ring-gray-600 dark:ring-gray-500 !ring-offset-2"
    }
  );
};

export const getApplicantStatusIconInfo = (status: string): string => {
  switch (status) {
    case "link_expired":
      return "text-red-800";
    case "pending_assessment":
      return "text-yellow-800";
    case "review_scheduled":
      return "text-blue-800";
    case "assessment_completed":
    case "hired":
      return "text-green-800";
    case "partial_interview":
    case "offer_declined":
      return "text-orange-800";
    case "aborted_interview":
    case "rejected":
    case "assessment_rejected":
    case "cv_rejected":
    case "ai_interview_rejected":
      return "text-red-800";
    case "form_incomplete":
    case "interview_sent":
      return "text-purple-800";
    default:
      return "text-gray-800";
  }
}

export const convertMarkdownToHtml = (markdown: string): string => {
  return marked(markdown, { breaks: true }) as string;
};

/**
 * Strip ElevenLabs-style `[audio tags]` (e.g. `[happy]`, `[laughs]`) from an
 * interview message. Removes the whitespace the tag leaves behind — no leading
 * space and no doubled internal spaces — and capitalizes the first letter, so
 * `"[happy] hello world"` → `"Hello world"` (not `" hello world"`).
 */
// Capitalize the first letter of every sentence: the start of the string, and
// the letter after a LONE sentence terminator (. ! ?) — an ellipsis ("..."/"..")
// is a mid-thought pause, so it does NOT start a new sentence
// ("[happy] alright... so" → "Alright... so", but "naturally. shall we?" →
// "Naturally. Shall we?").
const capitalizeSentences = (text: string): string =>
  text
    .replace(/^(\s*)([a-z])/, (_m, s, c) => s + c.toUpperCase())
    .replace(/([.!?]+)(\s+)([a-z])/g, (m, term, sp, c) =>
      term.length === 1 ? term + sp + c.toUpperCase() : m
    );

export const stripSpeechTags = (text: string): string => {
  const cleaned = (text ?? "")
    .replace(/\[[^\]]*\]/g, "") // drop the tags
    .replace(/\s{2,}/g, " ")     // collapse the gaps they leave
    .trim();
  return cleaned ? capitalizeSentences(cleaned) : cleaned;
};
/**
 * Compact a job location for list views. Locations often arrive as
 * "venue/company, city, country" — keep just the last two segments
 * ("city, country"); shorter values ("Singapore", "Jakarta, Indonesia")
 * pass through unchanged.
 */
export const shortLocation = (location?: string | null): string => {
  const parts = (location || "").split(",").map((p) => p.trim()).filter(Boolean);
  return parts.length > 2 ? parts.slice(-2).join(", ") : parts.join(", ");
};
